/**
 * Beans (ADR 0043, P5-LOOP-02; named by the founder on 2026-10-29): points that add up across every
 * game, earned by playing, and a level they raise. Never sold, never an advantage. Kept per person
 * in this browser like solo progress (ADR 0029): the guest's, and each signed-in person's.
 *
 * A play earns: 10 for finishing, 20 more for winning, 10 a star, the game's own (a few beans per
 * right answer, row or difference, from its manifest), 15 for the day's daily once a game, 10 for
 * the first play of the day; and with friends in a room, half as much again.
 *
 * Signed in, beans follow the person (#585, migration 0019): each browser keeps its own running
 * total for them under a random device id, and the api sums the person's devices. The wallet keeps
 * the other devices' part from the last sync, so the total shown is the person's.
 */

/** A game's own beans for one of its outcome stats (manifest.beans). */
export interface BeanRule {
  stat: string;
  each: number;
  max: number;
  label: string;
}

export interface Play {
  gameId: string;
  /** The play was decided by the game (not quit or broken). */
  decided: boolean;
  /** This player won (in Battle, they or their side, not just the match being decided). */
  won: boolean;
  stats: Record<string, number>;
  /** Today's daily, and played with friends in a room. */
  daily: boolean;
  together: boolean;
}

export interface Earned {
  total: number;
  parts: { label: string; beans: number }[];
}

export interface Wallet {
  total: number;
  /** The last UTC day beans were earned (for the first play of the day). */
  lastDay: string;
  /** "gameId:day" for each daily already counted. */
  dailies: string[];
  /** Signed in: the person's beans from their other devices, as of the last sync. */
  others: number;
  /** Signed in on their own browser: the guest's beans have joined theirs (once). */
  guestIn: boolean;
}

export const emptyWallet = (): Wallet => ({
  total: 0,
  lastDay: "",
  dailies: [],
  others: 0,
  guestIn: false,
});

/** The beans to show: this browser's, and the person's other devices'. */
export const beansOf = (wallet: Wallet) => wallet.total + wallet.others;

/** What a play earns, and the wallet after it. */
export function award(
  wallet: Wallet,
  play: Play,
  rules: readonly BeanRule[],
  day: string,
): { wallet: Wallet; earned: Earned } {
  const parts: { label: string; beans: number }[] = [];
  const add = (label: string, beans: number) => {
    if (beans > 0) parts.push({ label, beans: Math.round(beans) });
  };
  if (play.decided) add("Finished", 10);
  if (play.won) add("Won", 20);
  const stars = Math.min(3, Math.max(0, Math.round(play.stats["stars"] ?? 0)));
  add("★".repeat(stars), 10 * stars);
  for (const r of rules)
    add(r.label, Math.min(r.max, Math.max(0, (play.stats[r.stat] ?? 0) * r.each)));
  const dailyKey = `${play.gameId}:${day}`;
  const dailyNew = play.daily && !wallet.dailies.includes(dailyKey);
  if (dailyNew) add("Today's daily", 15);
  if (wallet.lastDay !== day) add("First play today", 10);
  const before = parts.reduce((n, p) => n + p.beans, 0);
  if (play.together) add("With friends", before / 2);
  const total = parts.reduce((n, p) => n + p.beans, 0);
  // Only this week's dailies are worth keeping to tell repeats apart.
  const dailies = dailyNew ? [...wallet.dailies, dailyKey].slice(-50) : wallet.dailies;
  return {
    wallet: { ...wallet, total: wallet.total + total, lastDay: day, dailies },
    earned: { total, parts },
  };
}

/** Level L starts at 50·L·(L−1) beans: 0, 100, 300, 600, 1,000 … a little further each time. */
export const levelStart = (level: number) => 50 * level * (level - 1);

/** A total's level, and how far into it: beans in, and beans the level takes. */
export function levelOf(total: number): { level: number; into: number; need: number } {
  let level = 1;
  while (levelStart(level + 1) <= total) level++;
  return {
    level,
    into: total - levelStart(level),
    need: levelStart(level + 1) - levelStart(level),
  };
}

const KEY = (profile: string | null) =>
  profile === null ? "obean.beans" : `obean.beans.${profile}`;

/** This browser's beans for the guest, or a signed-in person (their id). */
export function loadWallet(
  profile: string | null = null,
  storage: Pick<Storage, "getItem"> | null = safeStorage(),
): Wallet {
  try {
    const raw = storage?.getItem(KEY(profile));
    if (!raw) return emptyWallet();
    const w = JSON.parse(raw) as Partial<Wallet>;
    return {
      total: typeof w.total === "number" && w.total >= 0 ? Math.floor(w.total) : 0,
      lastDay: typeof w.lastDay === "string" ? w.lastDay : "",
      dailies: Array.isArray(w.dailies) ? w.dailies.filter((d) => typeof d === "string") : [],
      others: typeof w.others === "number" && w.others >= 0 ? Math.floor(w.others) : 0,
      guestIn: w.guestIn === true,
    };
  } catch {
    return emptyWallet();
  }
}

export function saveWallet(
  wallet: Wallet,
  profile: string | null = null,
  storage: Pick<Storage, "setItem"> | null = safeStorage(),
): void {
  try {
    storage?.setItem(KEY(profile), JSON.stringify(wallet));
  } catch {
    // Private windows and full storage: beans just aren't kept.
  }
}

/** This browser's id for a person's beans (random, kept here): what the api sums by. */
export function deviceId(
  profile: string,
  storage: Pick<Storage, "getItem" | "setItem"> | null = safeStorage(),
): string {
  const key = `obean.device.${profile}`;
  try {
    const known = storage?.getItem(key);
    if (known && /^[A-Za-z0-9_-]{8,40}$/.test(known)) return known;
  } catch {
    // No storage: a fresh id each time, so this browser's beans count once per visit at most.
  }
  const id = Array.from(
    crypto.getRandomValues(new Uint8Array(12)),
    (b) => "abcdefghijklmnopqrstuvwxyz0123456789"[b % 36],
  ).join("");
  try {
    storage?.setItem(key, id);
  } catch {
    // As above.
  }
  return id;
}

/**
 * A signed-in person's beans, synced (#585): this browser's total for them (with the guest's
 * joining once, on their own browser, as progress does: ADR 0029) goes to the api, and the other
 * devices' part comes back. Offline, the wallet as it is.
 */
export async function syncBeans(
  profile: string,
  ownBrowser: boolean,
  remote: { saveBeans: (device: string, total: number) => Promise<{ total: number }> },
  storage: Pick<Storage, "getItem" | "setItem"> | null = safeStorage(),
): Promise<Wallet> {
  let wallet = loadWallet(profile, storage);
  if (ownBrowser && !wallet.guestIn) {
    const guest = loadWallet(null, storage);
    wallet = { ...wallet, total: wallet.total + guest.total, guestIn: true };
  }
  try {
    const { total } = await remote.saveBeans(deviceId(profile, storage), wallet.total);
    wallet = { ...wallet, others: Math.max(0, total - wallet.total) };
  } catch {
    // Offline or signed out meanwhile: the next sync catches up.
  }
  saveWallet(wallet, profile, storage);
  return wallet;
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * Whether this player won: in Battle the winner's slot (stats.winner, slot + 1) or side
 * (stats.winnerTeam, team + 1), otherwise the result.
 */
export function wonBy(
  outcome: { result: "win" | "loss"; stats: Record<string, number> },
  slot: number,
  team: number | undefined,
): boolean {
  const winner = outcome.stats["winner"];
  const winnerTeam = outcome.stats["winnerTeam"];
  if (winnerTeam !== undefined) return team !== undefined && winnerTeam === team + 1;
  if (winner !== undefined) return winner === slot + 1;
  return outcome.result === "win";
}
